import React, {useEffect, useRef, useState} from "react";
import DriveFileRenameOutlineRoundedIcon from "@mui/icons-material/DriveFileRenameOutlineRounded";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteIcon from "@mui/icons-material/Delete";
import {
    Alert,
    Box,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Menu,
    MenuItem,
    Skeleton,
    TextField,
    Toolbar,
    Typography,
} from "@mui/material";
import {useChatHistoryList, useDeleteChat, useRenameChat} from "../../api/chat/chatQueries";
import {useTranslation} from "react-i18next";

type Props = {
    loadChat: (chatId: string | undefined) => void;
    onChatDeleted: (chatId: string) => void;
    selectedChatId: string | undefined;
};

export const chatHistoryDrawerWidth = "15vw";

const ChatHistoryComponent: React.FC<Props> = ({loadChat, onChatDeleted, selectedChatId}) => {
    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
    const [menuChatId, setMenuChatId] = useState<string | null>(null);
    const isMenuOpen = Boolean(menuAnchorEl);
    const [editingMenuItem, setEditingMenuItem] = useState<string | null>(null);

    const inputRef = useRef<HTMLFormElement | null>(null);
    const chatHistoryQuery = useChatHistoryList();
    const renameChatMutation = useRenameChat();
    const deleteChatMutation = useDeleteChat();
    const {t: translate} = useTranslation();
    const errorMessage =
        chatHistoryQuery.error?.message || renameChatMutation.error?.message || deleteChatMutation.error?.message;

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (inputRef.current && !inputRef.current.contains(e.target as Node)) {
                setEditingMenuItem(null);
            }
        };

        document.addEventListener("click", handleClickOutside);

        return () => {
            document.removeEventListener("click", handleClickOutside);
        };
    }, []);

    const handleChatHistorySelection = (chatId: string) => {
        loadChat(chatId);
    };

    const handleMenuOpen = (e: React.MouseEvent<HTMLElement>, chatId: string) => {
        e.stopPropagation();
        setMenuAnchorEl(e.currentTarget);
        setMenuChatId(chatId);
    };

    const handleMenuClose = (e: React.MouseEvent<HTMLElement>) => {
        if (e) {
            e.stopPropagation();
        }
        setMenuAnchorEl(null);
        setMenuChatId(null);
    };

    const enableEditingOnItem = (e: React.MouseEvent<HTMLElement>, chatId: string) => {
        e.stopPropagation();
        setEditingMenuItem(chatId);
        handleMenuClose(e);
    };

    const handleRename = async (e: React.FormEvent<HTMLFormElement>, chatId: string) => {
        e.preventDefault();
        const formData = new FormData(e.currentTarget);
        const newName = formData.get("chat_rename" + chatId) as string;

        if (newName.trim()) {
            try {
                await renameChatMutation.mutateAsync({chatId: chatId, newName});
                setEditingMenuItem(null);
            } catch {
                return;
            }
        }
    };

    const handleDelete = async (e: React.MouseEvent<HTMLElement>, chatId: string) => {
        handleMenuClose(e);
        try {
            await deleteChatMutation.mutateAsync({chatId: chatId});
            if (chatId === selectedChatId) {
                onChatDeleted(chatId);
            }
        } catch {
            return;
        }
    };

    const chatHistory = chatHistoryQuery.data ?? [];
    const isLoading = chatHistoryQuery.isLoading;
    const isError = chatHistoryQuery.isError;

    return (
        <Box>
            <Drawer
                className="chat-history-drawer"
                variant="permanent"
                sx={{
                    width: chatHistoryDrawerWidth,
                    flexShrink: 0,
                    "& .MuiDrawer-paper": {
                        width: chatHistoryDrawerWidth,
                        boxSizing: "border-box",
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                    },
                }}
            >
                <Toolbar sx={{flexShrink: 0}}/>
                <List sx={{flex: 1, overflowY: "auto", p: 0, m: 0}}>
                    {errorMessage ? (
                        <ListItem>
                            <Alert severity="error" sx={{width: "100%"}}>
                                {errorMessage}
                            </Alert>
                        </ListItem>
                    ) : null}
                    {isLoading ? (
                        Array.from(new Array(5)).map((_, index) => (
                            <Box key={index}>
                                <ListItem>
                                    <Skeleton variant="text" width="100%" height={30} />
                                </ListItem>
                                <Divider />
                            </Box>
                        ))
                    ) : isError ? null : !Array.isArray(chatHistory) || chatHistory.length === 0 ? (
                        <ListItem>
                            <Typography color="text.secondary">{translate("chatHistory.noHistory")}</Typography>
                        </ListItem>
                    ) : (
                        chatHistory.map((tupel, index) => (
                            <Box key={tupel.chatId || `error-${index}`}>
                                <ListItem
                                    disablePadding
                                    secondaryAction={
                                        <IconButton
                                            edge="end"
                                            onClick={(e) => handleMenuOpen(e, tupel.chatId)}
                                            aria-label={translate("chatHistory.moreActions")}
                                        >
                                            <MoreVertIcon />
                                        </IconButton>
                                    }
                                >
                                    <ListItemButton onClick={() => handleChatHistorySelection(tupel.chatId)}>
                                        {editingMenuItem === tupel.chatId ? (
                                            <form ref={inputRef} onSubmit={(e) => handleRename(e, tupel.chatId)}>
                                                <TextField
                                                    name={"chat_rename" + tupel.chatId}
                                                    defaultValue={tupel.title || tupel.title}
                                                    autoFocus
                                                    error={renameChatMutation.isError}
                                                />
                                            </form>
                                        ) : (
                                            <ListItemText
                                                primary={tupel.title || tupel.title}
                                                slotProps={{primary: {noWrap: true}}}
                                                sx={{minWidth: 0}}
                                            />
                                        )}
                                    </ListItemButton>
                                </ListItem>
                                <Divider />
                            </Box>
                        ))
                    )}
                </List>

                <Menu
                    anchorEl={menuAnchorEl}
                    open={isMenuOpen}
                    onClose={handleMenuClose}
                    anchorOrigin={{vertical: "top", horizontal: "left"}}
                    transformOrigin={{vertical: "top", horizontal: "right"}}
                >
                    <MenuItem
                        onClick={(e) => enableEditingOnItem(e, menuChatId || "")}
                        disabled={deleteChatMutation.isPending}
                    >
                        <DriveFileRenameOutlineRoundedIcon fontSize="small" sx={{mr: 1}} />
                        {translate("chatHistory.rename")}
                    </MenuItem>
                    <MenuItem
                        sx={{color: "red"}}
                        onClick={(e) => handleDelete(e, menuChatId || "")}
                        disabled={deleteChatMutation.isPending}
                    >
                        <DeleteIcon fontSize="small" sx={{mr: 1}} />
                        {translate("chatHistory.delete")}
                    </MenuItem>
                </Menu>
            </Drawer>
        </Box>
    );
};

export default ChatHistoryComponent;
